import { useCallback } from 'react';
import { z } from 'zod';
import cn from '../../../../utils/cn';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../atoms/form/Form';
import { useForm } from '../../../../hooks/use-form';
import Input from '../../../../atoms/inputs/input/Input';
import ButtonCTA, {
  ButtonCTASize,
  ButtonCTAVariant,
} from '../../../../atoms/buttons/button-cta/ButtonCTA';
import { LinkEditorState } from './tiptap-link';
import { TiptapRange } from '../../Tiptap';

const linkFormSchema = z.object({
  linkUrl: z.string().min(1, 'Le lien hypertexte ne peut pas être vide'),
  linkText: z.string().optional(),
});

type LinkForm = z.infer<typeof linkFormSchema>;

type TiptapLinkFormProps = {
  editorState: LinkEditorState;
  addLink: (linkUrl: string, linkText?: string) => void;
  updateLink: (range: TiptapRange, linkUrl: string, linkText?: string) => void;
  removeLink: () => void;
};

const TiptapLinkForm = ({
  editorState,
  addLink,
  updateLink,
  removeLink,
}: TiptapLinkFormProps) => {
  const onSubmit = useCallback(
    async (formValues: LinkForm) => {
      if (editorState.selectionIsLink) {
        updateLink(
          editorState.selectionLinkMarkRange,
          formValues.linkUrl,
          formValues.linkText,
        );

        return;
      }

      addLink(formValues.linkUrl, formValues.linkText);
    },
    [editorState, updateLink, addLink],
  );

  const form = useForm<LinkForm>({
    resolver: linkFormSchema,
    onSubmit,
    defaultValues: {
      linkUrl: editorState.selectionIsLink ? editorState.selectionLinkHref : '',
      linkText: editorState.selectionIsLink
        ? editorState.selectionLinkText
        : editorState.selectionText || '',
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          form.submit(e);
          e.stopPropagation();
        }}
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="linkUrl"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-xs font-medium text-neutral-500">
                  Lien hypertexte
                </FormLabel>
                <FormControl>
                  <Input.Root {...field}>
                    <Input.Input placeholder="https://www.la-boite-immo.com/" />
                    <Input.Clear onClick={() => field.onChange('')} />
                  </Input.Root>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="linkText"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-xs font-medium text-neutral-500">
                  Texte à afficher (optionnel)
                </FormLabel>
                <FormControl>
                  <Input.Root {...field}>
                    <Input.Input
                      placeholder={
                        form.watch('linkUrl') ? form.watch('linkUrl') : 'Non renseigné'
                      }
                    />
                    <Input.Clear onClick={() => field.onChange('')} />
                  </Input.Root>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div
          className={cn('flex items-center justify-between gap-2 [&>button]:w-fit', {
            'justify-end': !editorState.selectionIsLink,
          })}
        >
          {editorState.selectionIsLink ? (
            <>
              <ButtonCTA
                type="button"
                variant={ButtonCTAVariant.RED}
                size={ButtonCTASize.SMALL}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeLink();
                }}
              >
                Supprimer
              </ButtonCTA>
              <ButtonCTA type="submit" size={ButtonCTASize.SMALL}>
                Valider les modifications
              </ButtonCTA>
            </>
          ) : (
            <ButtonCTA
              aria-label="ajouter un lien"
              type="submit"
              size={ButtonCTASize.SMALL}
            >
              Ajouter un lien
            </ButtonCTA>
          )}
        </div>
      </form>
    </Form>
  );
};

export default TiptapLinkForm;
